import { useContext } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useUpdateAllCarrierMargins } from "~/features/quote/hooks/use-carrier-selection-hooks.ts";
import type { InsuranceFormData } from "~/shared/components/insurance-form/insurance-form.tsx";
import { InsuranceForm } from "~/shared/components/insurance-form/insurance-form.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useNotifications } from "~/shared/hooks/use-notifications.ts";

import { useAddInsurance, useRemoveInsurance } from "./add-insurance-hooks.tsx";

const name = "addInsurance";

type ModalProps = {
	showModal: boolean;
	onToggleModal: (name: string) => void;
	quoteId?: number;
	defaultProductType?: number;
	defaultShipmentValue?: number | null;
	hideApplyTo?: boolean;
	onSuccess?: () => void;
};

export const AddInsuranceModal = ({
	showModal,
	onToggleModal,
	quoteId = 0,
	defaultProductType,
	defaultShipmentValue,
	hideApplyTo = false,
	onSuccess,
}: ModalProps) => {
	const { isInternal } = useDeploymentType();
	const { quoteState } = useContext(QuoteDetailsContext);
	const {
		handleAddInsurance: addInsurance,
		isPending,
		refreshModal: addInsuranceRefreshModal,
	} = useAddInsurance();
	const {
		handleRemoveInsurance: removeInsurance,
		isPending: isRemovePending,
		refreshModal: removeInsuranceRefreshModal,
	} = useRemoveInsurance();
	const { showSuccess } = useNotifications();
	const {
		updateAllCarrierMargins,
		isPending: isUpdatingMargins,
		refreshModal: updateMarginsRefreshModal,
	} = useUpdateAllCarrierMargins();

	useAutoSpinner(isPending || isRemovePending || isUpdatingMargins);

	const handleClose = () => {
		onToggleModal(name);
	};

	const handleSubmit = async (formData: InsuranceFormData) => {
		let updatedETag = quoteState.eTag;

		// Saving carrier margins is an internal-only operation
		if (isInternal) {
			const marginETag = await updateAllCarrierMargins(
				quoteState.id,
				quoteState.eTag,
				quoteState.carrierPrices,
			);
			if (!marginETag) {
				return;
			}
			updatedETag = marginETag;
		}
		onToggleModal(name);

		let currentETag = updatedETag;

		// Insurance is only "applied" when insuranceApplyTypeId is set. A
		// shipment value / product type can be present without active coverage
		// (e.g. after Remove Insurance, which retains those fields), so key the
		// pre-remove on insuranceApplyTypeId to avoid a redundant remove call.
		if (quoteState.insuranceApplyTypeId !== null) {
			const removeResult = await removeInsurance({
				eTag: currentETag,
				id: quoteId,
			});
			if (removeResult?.success && removeResult.data?.eTag) {
				currentETag = removeResult.data.eTag;
			}
		}

		const result = await addInsurance({
			...formData,
			eTag: currentETag,
			id: quoteId,
			selectedCarrierPriceId: quoteState.selectedCarrierPriceId,
		});
		if (result?.success) {
			showSuccess("Insurance added successfully.");
			onSuccess?.();
		}
	};

	if (!showModal) return null;

	return (
		<>
			{addInsuranceRefreshModal}
			{removeInsuranceRefreshModal}
			{updateMarginsRefreshModal}
			<InsuranceForm
				hideApplyTo={hideApplyTo}
				initialValues={{
					product: defaultProductType,
					shipmentValue: defaultShipmentValue,
				}}
				onClose={handleClose}
				onSubmit={(formData) => {
					void handleSubmit(formData);
				}}
			/>
		</>
	);
};
